import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { StateMachineService } from './services/state-machine.service';
import { FirestoreConnectorService } from './services/firestore-connector.service';
import { AzureConnectorService } from './services/azure-connector.service';
import { AppMode, CFMObj, FormValues, Overrides } from '@aaa/interface-agentScheduler';
import { JwtAuthService } from '../../services/jwt-auth';
import { Subscription } from 'rxjs';
import { AnalyticsService, AnalyticsEvent } from '../../services/analytics';
import { environment } from '../../../environments/environment';
import { FormService } from './services/form.service';
import { WINDOW } from '@ng-web-apis/common';

@Component({
  selector: 'ava-agent-scheduler',
  templateUrl: './agent-scheduler.component.html',
})
export class AgentSchedulerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() params: string | undefined;
  @ViewChild('container') container: ElementRef | undefined;
  containerWidth: number | undefined;
  agentCount: number | undefined;
  uid: number | undefined;
  JSON: JSON = JSON;
  formId: string | undefined;

  pageChangeSub: Subscription | undefined;
  environment = environment;

  constructor(
    @Inject(WINDOW)
    private window: Window,
    public acs: AzureConnectorService,
    public fcs: FirestoreConnectorService,
    public jwt: JwtAuthService,
    public sms: StateMachineService,
    private analyticsService: AnalyticsService,
    public formService: FormService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.formId = '1234'; // todo: generate random string

    let paramsString = this.params;
    if (this.environment.ngServe) {
      paramsString = this.testParamsString;
    }
    if (paramsString) {
      const paramsObject = JSON.parse(paramsString);

      if (paramsObject) {
        const appMode: AppMode = paramsObject.meta?.appMode ? paramsObject.meta.appMode : AppMode.TRAVEL;

        this.agentCount = paramsObject?.agents?.length;
        this.uid = paramsObject?.meta?.userId;

        const urlParams = new URLSearchParams(this.window.location.search);
        let cfmObj: CFMObj = {
          selectedTopics: [],
          region: '',
          meeting: '',
          timeSlot: undefined,
          appointmentId: '',
        };

        const cfmEncodedString = urlParams.get('cfm');
        if (cfmEncodedString) {
          const cfmDecodedString = atob(cfmEncodedString);
          cfmObj = JSON.parse(cfmDecodedString);
        }

        const overrides: Overrides = {
          promo: urlParams.get('promo') || '',
          agentEmail: urlParams.get('agentEmail')?.toLowerCase() || '',
          cfm: !!cfmEncodedString,
          appointmentId: cfmObj.appointmentId,
        };

        this.formService.formSetup(this.formId, paramsObject, appMode, overrides, cfmObj);
        this.setupServices(paramsObject, overrides, this.formService.formValues[this.formId]);

        this.pageChangeSub = this.sms.pageChangeSubject.subscribe(async (pageChange) => {
          if (pageChange?.event !== 'NEXT') return;
          // https://stackoverflow.com/a/56971002
          // makes this work on FF, not exactly sure why
          await new Promise((r) => setTimeout(r, 0));
          this.container?.nativeElement.scrollIntoView({ behavior: 'smooth' });

          if (!overrides.cfm) {
            if (pageChange.page) {
              const page_path =
                window.location.pathname.toLowerCase() +
                (overrides.agentEmail ? '/agent_flow/' : '/default_flow/') +
                pageChange.page;
              this.analyticsService.sendPage(page_path);
            }

            if (pageChange.page === 'confirmation') {
              const event: AnalyticsEvent = {
                eventCategory: overrides.agentEmail ? 'travel_agent_scheduler_agent' : 'travel_agent_scheduler_default',
                eventAction: 'appointment',
                eventLabel: appMode.toLocaleLowerCase(),
                eventValue: 1,
              };
              this.analyticsService.sendEvent(event);
            }
          }
        });
      }
    }

    const eventCustomElement: AnalyticsEvent = {
      eventCategory: 'Custom Element',
      eventAction: 'Supported',
      eventLabel:
        '<ava-agent-scheduler>' + window.location.origin.toLowerCase() + window.location.pathname.toLowerCase(),
      eventValue: 1,
    };
    this.analyticsService.sendEvent(eventCustomElement);
  }

  ngAfterViewInit(): void {
    this.onResize();
  }

  ngOnDestroy(): void {
    if (this.formId) {
      delete this.formService.form[this.formId];
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.containerWidth = this.container?.nativeElement.getBoundingClientRect().width;
  }

  private setupServices(paramsObj: any, overrides: Overrides, formValue: FormValues) {
    this.acs.setupService(overrides);
    if (this.formId) {
      this.sms.setupService(this.formId, formValue);
    }
  }

  get testParamsString(): string {
    return `
{"meta":{"clubId":"071","user":{"firstName":"Admin","lastName":"User","email":"adminuser@aaashare.com","memberNumber":"4290711234567892","roles":["authenticated user","administrator"],"userId":"1","token":"eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJpc3MiOiJhdmFnYXRlLXdzcy0xQGFwcHNwb3QuZ3NlcnZpY2VhY2NvdW50LmNvbSIsInN1YiI6ImF2YWdhdGUtd3NzLTFAYXBwc3BvdC5nc2VydmljZWFjY291bnQuY29tIiwiYXVkIjoiaHR0cHM6XC9cL2lkZW50aXR5dG9vbGtpdC5nb29nbGVhcGlzLmNvbVwvZ29vZ2xlLmlkZW50aXR5LmlkZW50aXR5dG9vbGtpdC52MS5JZGVudGl0eVRvb2xraXQiLCJpYXQiOjE2NTMwNjk3MDUsImV4cCI6MTY1MzA3MzMwNSwidWlkIjoiY2x1YklkOjA3MTt1c2VySWQ6MSIsImNsYWltcyI6eyJyb2xlcyI6WyJhdXRoZW50aWNhdGVkIHVzZXIiLCJhZG1pbmlzdHJhdG9yIl0sIm1lbWJlck51bWJlciI6IjQyOTA3MTEyMzQ1Njc4OTIifX0.VBMmmFvCJsupJEe1RnBZ1zfTeDYAxN59An6KV9z_EpBL47wgvp6tut47W481cXM5Kpvhgy0ngrdd53_ttkaxwsfFSsMEGrJY7306iYeeoZ69Cvj9Gf5JRhmHT69DzLkEHra8OVi_8qPadOXbGsdqnq52kGGYODPIjnc7X2xWjH_ysWgdR-CEOQW2-d3jYYS_YE6gULx5VNZit1FgYNxgxyVQvXaPerFaLdmko8rslAjtxllnRIEajmu-44r7XLu2gzDv60i2mRgPX_vfksi1qd7jk7eUlNjfMvGw5oslSix9EVOiTHIi7IQtF90mpYjfQx5345P6Eo0kUcNJqVE69Q"},"webEnv":"PROD","sessionId":"hliGV2Worp4ab2wRZJ2VnevvMxnsZaAVN-VTSw7re-w","tenantId":"522ebbbe-549c-4a37-b964-e52c4b20e0f9","appMode":"INSURANCE"},"agents":[{"fName":"Eileen","lName":"Donia","email":"edonia@AAASJ.COM","typeIds":["3379"],"about":"As a local AAA South Jersey Insurance Agent, I\u2019m happy to provide you with trusted guidance on your current insurance plans, customize a new coverage plan that\u2019s right for your needs, and help you take advantage of all the discounts available to you. Contact me today for a policy review, or for a free no-obligation auto or home insurance quote\\r","phone":"856-679-2663","image":"https:\\/\\/www.southjersey.aaa.com\\/sites\\/default\\/files\\/styles\\/agent_search_result\\/public\\/E_Donia.jpg?itok=NT8TWyKi","officeIds":["9506"],"topicIds":["3399"],"regionIds":["3393"],"meetingIds":["3381"],"shareWithIds":["3388"]},{"fName":"Eileen","lName":"Brennan","email":"EBrennan@aaasj.com","typeIds":["3379"],"about":"As a local AAA South Jersey Insurance Agent, I\u2019m happy to provide you with trusted guidance on your current insurance plans, customize a new coverage plan that\u2019s right for your needs, and help you take advantage of all the discounts available to you. Contact me today for a policy review, or for a free no-obligation auto or home insurance quote\\r","phone":"856-679-2628","image":"https:\\/\\/www.southjersey.aaa.com\\/sites\\/default\\/files\\/styles\\/agent_search_result\\/public\\/E_Brennan.jpg?itok=_1jlnj_y","officeIds":["8362"],"topicIds":["3391","3392","3398","3400","3399"],"regionIds":["3394"],"meetingIds":["3380","3381"],"shareWithIds":["3388"]},{"fName":"Chelynne","lName":"Torres","email":"ctorres@aaasj.com","typeIds":["3379"],"about":"As a local AAA South Jersey Insurance Agent, I\u2019m happy to provide you with trusted guidance on your current insurance plans, customize a new coverage plan that\u2019s right for your needs, and help you take advantage of all the discounts available to you. Contact me today for a policy review, or for a free no-obligation auto or home insurance quote\\r","phone":"856-679-2627","image":"https:\\/\\/www.southjersey.aaa.com\\/sites\\/default\\/files\\/styles\\/agent_search_result\\/public\\/Chelynne400.jpg?itok=mGw--Nar","officeIds":["9506"],"topicIds":["3391","3392","3398","3399"],"regionIds":["3394"],"meetingIds":["3380"],"shareWithIds":["3388"]},{"fName":"Sean","lName":"Carmody","email":"scarmody@aaasj.com","typeIds":["3379"],"about":"As a local AAA South Jersey Insurance Agent, I\u2019m happy to provide you with trusted guidance on your current insurance plans, customize a new coverage plan that\u2019s right for your needs, and help you take advantage of all the discounts available to you. Contact me today for a policy review, or for a free no-obligation auto or home insurance quote\\r","phone":"856-679-2625","image":"https:\\/\\/www.southjersey.aaa.com\\/sites\\/default\\/files\\/styles\\/agent_search_result\\/public\\/Sean%20Carmondy.jpg?itok=LuRuAPLQ","officeIds":["8364"],"topicIds":["3391","3392","3398"],"regionIds":["3393","3394"],"meetingIds":["3380"],"shareWithIds":["3388"]}],"offices":[{"id":"8362","name":"Voorhees Office","addrLine1":"700 Laurel Oak Road","addrLine2":"Voorhees, NJ 08043","geoLoc":["39.845284800000","-74.982777800000"]},{"id":"8363","name":"Millville Office","addrLine1":"2190 North 2nd Street","addrLine2":"Millville, NJ 08332","geoLoc":["39.423211400000","-75.040522800000"]},{"id":"8364","name":"Sewell Office","addrLine1":"380 Egg Harbor Rd","addrLine2":"Sewell, NJ 08080","geoLoc":["39.753035056602","-75.075641870499"]},{"id":"9506","name":"Cherry Hill Office","addrLine1":"2000 Marlton Pike W","addrLine2":"Cherry Hill, NJ 08002","geoLoc":["39.920679690676","-75.034628257672"]}],"meetings":[{"id":"3380","text":"Phone","response":"","default":"0","online":0,"type":"phone"},{"id":"3381","text":"In-person","response":"","default":"0","online":0,"type":"inPerson"}],"regions":[{"id":"3393","text":"New Quote","response":"","default":"0"},{"id":"3394","text":"Existing Policy","response":"","default":"0"}],"topics":[{"id":"3391","text":"Auto","icon":"icon-icon-automotive"},{"id":"3392","text":"Home","icon":"icon-home"},{"id":"3398","text":"Motorcycle \\/ RV \\/ Boat","icon":"icon-motorcycle"},{"id":"3400","text":"Commercial","icon":"icon-office"},{"id":"3401","text":"Life","icon":"icon-family1"},{"id":"3399","text":"Umbrella","icon":"icon-icon-umbrella"}],"types":[{"id":"3378","text":"Travel","response":""},{"id":"3379","text":"Insurance","response":""}],"shareWith":[{"id":"3388","email":""}]}
    `;
  }
}
